/**
 * Menu landing page — first view after login.
 *
 * Grid of report cards organised by category. Click → set active view
 * to that report's page. Solves the "16 tabs don't fit" problem and
 * gives a clear first-impression of platform breadth.
 */
import { useLang } from '@/i18n/useLang';

export type ReportKey =
  | 'janma-kundli'
  | 'complete-profile'
  | 'career' | 'bnn' | 'karmic' | 'health'
  | 'compatibility' | 'varshaphal' | 'vidya'
  | 'wealth' | 'manglik' | 'visa' | 'business'
  | 'progeny' | 'legal' | 'sade-sati'
  | 'rectify' | 'career-compat'
  | 'business-partner-compat' | 'bala' | 'pitra-dosha';

interface Card {
  key: ReportKey;
  emoji: string;
  title_hi: string;
  title_en: string;
  desc_hi: string;
  desc_en: string;
}

interface Group {
  title_hi: string;
  title_en: string;
  cards: Card[];
}

const _GROUPS: Group[] = [
  {
    title_hi: 'मूल कुण्डली',
    title_en: 'Foundation',
    cards: [
      { key: 'janma-kundli', emoji: '📜',
        title_hi: 'जन्म-कुण्डली (निःशुल्क)',
        title_en: 'Janma Kundli (Free)',
        desc_hi: 'सम्पूर्ण शास्त्रीय गणना — ग्रह-स्थिति, वर्ग-चार्ट, विंशोत्तरी एवं योगिनी दशा, अष्टकवर्ग, षड्बल। द्विभाषी; मानक एवं प्रो लेआउट।',
        desc_en: 'Full classical computation — positions, divisional charts, Vimshottari + Yoginī Dashās, Ashtakavarga, Shadbala. Bilingual; standard or pro layout.' },
    ],
  },
  {
    title_hi: 'सम्पूर्ण रिपोर्ट (फ्लैगशिप)',
    title_en: 'Complete Profile (flagship)',
    cards: [
      { key: 'complete-profile', emoji: '📜',
        title_hi: 'सम्पूर्ण ज्योतिषीय रिपोर्ट',
        title_en: 'Complete Astrological Profile',
        desc_hi: 'एक ही दस्तावेज़ में सब-कुछ — जन्म-कुण्डली, करियर, धन, स्वास्थ्य, वर्षफल, कर्म-पथ, सन्तान, विद्या, मंगल, विसा, क़ानूनी एवं अधिक। 30 पृष्ठ।',
        desc_en: 'Everything in one PDF — birth chart, career, wealth, health, varshaphal, karmic path, progeny, vidya, manglik, visa, legal, and more. ~30 pages.' },
    ],
  },
  {
    title_hi: 'करियर एवं शिक्षा',
    title_en: 'Career & Education',
    cards: [
      { key: 'career', emoji: '💼',
        title_hi: 'करियर रिपोर्ट (विस्तृत)', title_en: 'Career Report (detailed)',
        desc_hi: 'पूर्ण ज्योतिषीय विश्लेषण — Pass A + Pass B + LLM कथन',
        desc_en: 'Full astro analysis — Pass A + Pass B + LLM narration' },
      { key: 'business', emoji: '🚀',
        title_hi: 'व्यवसाय योग्यता', title_en: 'Business Suitability',
        desc_hi: 'नौकरी बनाम स्व-व्यवसाय; अनुशंसित क्षेत्र',
        desc_en: 'Job vs business; recommended sectors' },
      { key: 'business-partner-compat', emoji: '🤝',
        title_hi: 'व्यापार-साझेदारी संगति', title_en: 'Business Partner Compatibility',
        desc_hi: 'नवतारा + सम्बन्ध-अक्ष + संयुक्त-दशा + पूँजी-सिफ़ारिश',
        desc_en: 'Navatāra + sambandha + joint-dasha + equity recommendation' },
      { key: 'vidya', emoji: '🎓',
        title_hi: 'विद्या योग (बच्चों के लिए)', title_en: 'Vidya Yoga (for children)',
        desc_hi: 'शैक्षिक धारा, सीखने की शैली, माता-पिता मार्गदर्शन',
        desc_en: 'Education stream, learning style, parental guidance' },
    ],
  },
  {
    title_hi: 'विवाह एवं परिवार',
    title_en: 'Marriage & Family',
    cards: [
      { key: 'compatibility', emoji: '💞',
        title_hi: 'कुण्डली मिलान (शास्त्रीय)', title_en: 'Compatibility (Classical)',
        desc_hi: 'अष्टकूट 36-गुण + तीन प्रमुख दोष',
        desc_en: 'Ashtakoot 36-गुण + three key doshas' },
      { key: 'career-compat', emoji: '💼💞',
        title_hi: 'करियर+विवाह मिलान', title_en: 'Career+Marriage Compatibility',
        desc_hi: 'शास्त्रीय + आधुनिक — आय, स्थिरता, करियर-दिशाएँ',
        desc_en: 'Classical + modern — earning, stability, career directions' },
      { key: 'manglik', emoji: '🔴',
        title_hi: 'मंगल दोष विश्लेषण', title_en: 'Manglik Analysis',
        desc_hi: 'गंभीरता + न्यूनीकरण-कारक + उपाय',
        desc_en: 'Severity + mitigation factors + remedies' },
      { key: 'progeny', emoji: '👶',
        title_hi: 'संतान योग', title_en: 'Progeny Yoga',
        desc_hi: 'पंचम भाव विश्लेषण; सर्वोत्तम समय',
        desc_en: '5th house analysis; best window' },
    ],
  },
  {
    title_hi: 'धन एवं समय',
    title_en: 'Wealth & Timing',
    cards: [
      { key: 'wealth', emoji: '💰',
        title_hi: 'धन योग', title_en: 'Wealth Yoga',
        desc_hi: 'आय-स्रोत, ऋण-जोखिम, सम्पत्ति-समय',
        desc_en: 'Earning sources, debt risk, property timing' },
      { key: 'varshaphal', emoji: '🗓️',
        title_hi: 'वार्षिक भविष्यवाणी', title_en: 'Annual Forecast',
        desc_hi: '12-माह दशा-आधारित आउटलुक',
        desc_en: '12-month dasha-based outlook' },
      { key: 'sade-sati', emoji: '🪐',
        title_hi: 'साढ़ेसाती विश्लेषण', title_en: 'Sade Sati Detail',
        desc_hi: '3-चरण तिथि-आधारित विश्लेषण',
        desc_en: '3-phase date-by-date analysis' },
    ],
  },
  {
    title_hi: 'स्वास्थ्य एवं कर्म',
    title_en: 'Health & Karmic',
    cards: [
      { key: 'health', emoji: '🩺',
        title_hi: 'स्वास्थ्य विश्लेषण', title_en: 'Health Analysis',
        desc_hi: 'दोष-प्रकृति + संवेदनशील क्षेत्र + जीवनशैली',
        desc_en: 'Dosha + vulnerability map + lifestyle' },
      { key: 'karmic', emoji: '🔱',
        title_hi: 'कर्म-पथ विश्लेषण', title_en: 'Karmic Path Analysis',
        desc_hi: 'पूर्व-जन्म, वर्तमान विषय, अंक-शास्त्र',
        desc_en: 'Past-life, present themes, numerology' },
      // BNN tile removed in v2: BNN reading (Saturn-axis) is now folded
      // into the career report. Engine remains callable for n8n / legacy.
    ],
  },
  {
    title_hi: 'अन्य',
    title_en: 'Other',
    cards: [
      { key: 'visa', emoji: '✈️',
        title_hi: 'विदेश योग', title_en: 'Foreign Settlement',
        desc_hi: 'विदेश-प्रवास का प्रबल योग; दिशा-संकेत',
        desc_en: 'Foreign settlement strength; direction hint' },
      { key: 'legal', emoji: '⚖️',
        title_hi: 'न्यायिक योग', title_en: 'Legal Yoga',
        desc_hi: 'विवाद-दृष्टिकोण, अनुकूल समय',
        desc_en: 'Litigation outlook, favourable window' },
      { key: 'rectify', emoji: '🎯',
        title_hi: 'जन्म-समय शुद्धिकरण', title_en: 'Birth-Time Rectification',
        desc_hi: 'जीवन-घटनाओं से समय का सुधार',
        desc_en: 'Refine birth-time using life events' },
      { key: 'bala', emoji: '🪐',
        title_hi: 'बल विवेचना (षड्बल)',
        title_en: 'Bala Vivecana (Ṣaḍbala)',
        desc_hi: 'सम्पूर्ण शास्त्रीय गणना — षड्बल, भाव-बल, इष्ट/कष्ट-फल। 4 दृश्य चार्ट।',
        desc_en: 'Full classical computation — Ṣaḍbala, bhāva-bala, Iṣṭa/Kaṣṭa. 4 visual charts.' },
      { key: 'pitra-dosha', emoji: '🪔',
        title_hi: 'पितृ-दोष विश्लेषण',
        title_en: 'Pitṛ-doṣa Analysis',
        desc_hi: 'आठ शास्त्रीय संकेतों का परीक्षण; केवल कर्म-स्तरीय निर्देश; गम्भीर स्थिति में ज्योतिषी-परामर्श।',
        desc_en: 'Eight classical signature check; karma-level guidance only; serious cases route to astrologer consultation.' },
    ],
  },
];

export function MenuPage({ onSelect }: { onSelect: (k: ReportKey) => void }) {
  const { lang } = useLang();
  const t = (hi: string, en: string) => (lang === 'hi' ? hi : en);
  return (
    <div className="mx-auto max-w-screen-xl px-4 py-6 lg:px-8 lg:py-10">
      <header className="mb-8">
        <h1 className="text-2xl font-bold text-saffron-100 lg:text-3xl">
          {t('ज्योतिषीय रिपोर्ट केंद्र', 'Astrology Report Center')}
        </h1>
        <p className="mt-2 text-sm text-stone-400">
          {t(
            'जिस रिपोर्ट को बनाना चाहें, उस पर क्लिक करें। सभी रिपोर्ट 1-2 मिनट में तैयार होती हैं।',
            'Click on the report you want to generate. All reports complete in 1-2 minutes.',
          )}
        </p>
      </header>

      <div className="space-y-8">
        {_GROUPS.map((g) => (
          <section key={g.title_en}>
            <h2 className="mb-3 text-sm font-semibold uppercase tracking-wider text-saffron-400">
              {t(g.title_hi, g.title_en)}
            </h2>
            <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
              {g.cards.map((c) => (
                <button
                  key={c.key}
                  type="button"
                  onClick={() => onSelect(c.key)}
                  className="group flex flex-col items-start gap-2 rounded-2xl border border-stone-800 bg-stone-900/60 p-5 text-left ring-1 ring-stone-800 transition hover:border-saffron-700/60 hover:bg-stone-900/90 hover:ring-saffron-700/40"
                >
                  <span className="text-2xl">{c.emoji}</span>
                  <h3 className="text-base font-semibold text-stone-100 group-hover:text-saffron-200">
                    {t(c.title_hi, c.title_en)}
                  </h3>
                  <p className="text-xs leading-relaxed text-stone-400">
                    {t(c.desc_hi, c.desc_en)}
                  </p>
                </button>
              ))}
            </div>
          </section>
        ))}
      </div>
    </div>
  );
}
